'use client'

import React, { useRef, useEffect, useState } from 'react'
import styles from './timeline.module.scss'

interface TimelineEntry {
  title: string
  content: React.ReactNode
}

interface TimelineProps {
  data: TimelineEntry[]
  title?: string
  subtitle?: string
  theme?: 'light' | 'dark'
}

export const Timeline: React.FC<TimelineProps> = ({
  data,
  theme = 'light',
}) => {
  const [scrollProgress, setScrollProgress] = useState(0)
  const [timelineHeight, setTimelineHeight] = useState(0)
  const [activeMarkers, setActiveMarkers] = useState<boolean[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)
  const markerRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !timelineRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const containerTop = containerRect.top
      const containerHeight = containerRect.height
      const windowHeight = window.innerHeight

      // Calculate scroll progress
      const scrollStart = windowHeight * 0.1
      const scrollEnd = windowHeight * 0.5

      let progress = 0
      if (containerTop <= scrollStart) {
        progress = Math.min(
          1,
          (scrollStart - containerTop) / (containerHeight - scrollEnd)
        )
      }

      setScrollProgress(Math.max(0, Math.min(1, progress)))

      // Calculate which markers should be active
      const timelineRect = timelineRef.current.getBoundingClientRect()
      const timelineTop = timelineRect.top
      const progressLineHeight = progress * timelineHeight

      const newActiveMarkers = markerRefs.current.map((markerRef) => {
        if (!markerRef) return false

        const markerRect = markerRef.getBoundingClientRect()
        const markerRelativeTop = markerRect.top - timelineTop

        // Check if progress line has passed this marker
        return progressLineHeight >= markerRelativeTop + 20 // 20px offset for marker center
      })

      setActiveMarkers(newActiveMarkers)
    }

    // Calculate timeline height
    if (timelineRef.current) {
      setTimelineHeight(timelineRef.current.offsetHeight)
    }

    // Initialize marker refs array
    markerRefs.current = markerRefs.current.slice(0, data.length)

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll)
  }, [timelineHeight, data.length])

  return (
    <div className={`${styles.timeline} ${styles[theme]}`} ref={containerRef}>
      {/* <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </div> */}

      <div className={styles.content} ref={timelineRef}>
        {data.map((item, index) => (
          <div key={index} className={styles.timelineItem}>
            <div className={styles.timelineMarker}>
              <div
                ref={(el) => {
                  markerRefs.current[index] = el
                }}
                className={`${styles.markerOuter} ${
                  activeMarkers[index] ? styles.markerActive : ''
                }`}
              >
                <div
                  className={`${styles.markerInner} ${
                    activeMarkers[index] ? styles.markerInnerActive : ''
                  }`}
                />
              </div>
              <h3 className={styles.timelineTitle}>{item.title}</h3>
            </div>

            <div className={styles.timelineContent}>
              <h3 className={styles.timelineTitleMobile}>{item.title}</h3>
              <div className={styles.contentWrapper}>{item.content}</div>
            </div>
          </div>
        ))}

        <div
          className={styles.timelineLine}
          style={{ height: `${timelineHeight}px` }}
        >
          <div
            className={styles.progressLine}
            style={{
              height: `${scrollProgress * timelineHeight}px`,
              opacity: scrollProgress > 0 ? 1 : 0,
            }}
          />
        </div>
      </div>
    </div>
  )
}
